<template>
    <div class="granularity-container">
        <input-with-label for-id="granularitySelect">
            <template #label>
                Granularity:
                <span v-tooltip.top="'This will only reduce the granularity of a series.'">
                    <font-awesome-icon class="info-icon" :icon="['fas', 'question']" />
                </span>
                <info-icon-with-tooltip
                    v-if="showIndexGranularityWarning"
                    :tooltip-text="indexGranularityWarningText"
                    variant="warning"
                    direction="top"
                />
            </template>
            <select-button
                class="select-option-flex"
                id="granularitySelect"
                :options="aggregateGranularities"
                :model-value="aggregate.granularity ?? 'none'"
                option-label="name"
                option-value="id"
                :allow-empty="false"
                @update:model-value="updateAggregate($event === 'none' ? null : $event, 'granularity')"
                :disabled="props.selectionLocked"
            />
        </input-with-label>
        <input-with-label label="Type:" for-id="typeSelect">
            <div>
                <hover-tooltip v-if="aggregate.granularity === 'none'" :tooltipText="'This will only reduce the granularity of a series'" />
                <div class="desktop-only">
                    <select-button
                        class="select-option-flex"
                        id="typeSelect"
                        :options="aggregateTypes"
                        :model-value="aggregate.type ?? 'none'"
                        option-label="name"
                        option-value="id"
                        :allow-empty="false"
                        :disabled="aggregate.granularity === 'none' || props.selectionLocked"
                        @update:model-value="updateAggregate($event === 'none' ? null : $event, 'type')"
                    />
                </div>
                <div class="mobile-only">
                    <primevue-select
                        :disabled="aggregate.granularity === 'none' || props.selectionLocked"
                        :options="aggregateTypes"
                        @update:model-value="updateAggregate($event === 'none' ? null : $event, 'type')"
                        fluid
                        option-label="name"
                        option-value="id"
                        placeholder="Select a type"
                        :model-value="aggregate.type ?? 'none'"
                    />
                </div>
            </div>
        </input-with-label>
        <input-with-label for-id="cumulativeSelect">
            <template #label>
                Cumulative:
                <span v-tooltip.top="'Calculate the cumulative series for the data aggregated as specified above. MW series are converted to MWh.'">
                    <font-awesome-icon class="info-icon" :icon="['fas', 'question']" />
                </span>
            </template>
            <select-button
                :modelValue="aggregate.cumulative ?? false"
                id="cumulativeSelect"
                :options="switchOptions"
                option-label="title"
                option-value="value"
                :allow-empty="false"
                :disabled="!!(aggregate.delta) || props.selectionLocked"
                @update:model-value="updateCumulative"
            />
        </input-with-label>
        <input-with-label for-id="deltaSelect">
            <template #label>
                Delta:
                <span v-tooltip.top="'Calculate the delta series for the data aggregated as specified above. Each point is displayed as the difference to a point in the past. The point to compare to is the number of points in the past that you select.'">
                    <font-awesome-icon class="info-icon" :icon="['fas', 'question']" />
                </span>
            </template>
            <div class="delta-input">
                <select-button
                    :modelValue="useDelta"
                    id="deltaSelect"
                    :options="switchOptions"
                    option-label="title"
                    option-value="value"
                    :allow-empty="false"
                    :disabled="aggregate.cumulative || props.selectionLocked"
                    @update:model-value="updateUseDelta"
                />
                <input-number :disabled="!useDelta || props.selectionLocked" v-model="aggregate.delta" small show-buttons type="number" />
            </div>
        </input-with-label>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { IFlagOption } from '@/interfaces/general/IFlagOption.ts';
import { aggregateGranularities, aggregateTypes } from '@/constants/powerMarkets/ChartAggregateOptions.ts';
import InputNumber from 'primevue/inputnumber';
import SelectButton from 'primevue/selectbutton';
import HoverTooltip from '@/components/powerMarkets/common/HoverTooltip.vue';
import { cloneDeep } from '@/utils/global.ts';
import primevueSelect from 'primevue/select';
import { ICombinedAggregateOptions } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import InputWithLabel from '@/components/common/InputWithLabel.vue';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { isSupportedIndexGranularity } from '@/utils/benchmarks/indexUtil.ts';

const aggregate = ref<ICombinedAggregateOptions>({
    granularity: null,
    type: null,
    cumulative: false,
    delta: null,
});

const props = defineProps<{
    modelValue: ICombinedAggregateOptions;
    selectionLocked?: boolean;
    indexGranularityWarning?: string;
}>();

const switchOptions: Array<IFlagOption> = [
    {
        title: 'Yes',
        value: true,
    },
    {
        title: 'No',
        value: false,
    },
];

const useDelta = ref<boolean>(false);
const showIndexGranularityWarning = computed<boolean>(() => (
    !!props.indexGranularityWarning
    && !!aggregate.value.granularity
    && !isSupportedIndexGranularity(aggregate.value.granularity)
));
const indexGranularityWarningText = computed<string>(() => (
    props.indexGranularityWarning ?? ''
));

function updateAggregate (option: string, key: 'granularity' | 'type'): void {
    aggregate.value[key] = option;
}

function updateCumulative (option: boolean): void {
    aggregate.value.cumulative = option;
}

const emit = defineEmits<{
    'update:modelValue': [ICombinedAggregateOptions];
}>();

function updateUseDelta (value: boolean): void {
    useDelta.value = value;
    aggregate.value.delta = value ? 1 : null;
}

function setValuesFromModelValue (): void {
    if (!props.modelValue) return;
    if (JSON.stringify(props.modelValue) === JSON.stringify(aggregate.value)) return;
    aggregate.value = cloneDeep(props.modelValue);
    if (aggregate.value.delta) useDelta.value = true;
}

function emitDefaultValues (): void {
    if (JSON.stringify(aggregate.value) === JSON.stringify(props.modelValue)) return;
    const newAggregate = cloneDeep(aggregate.value);
    if (aggregate.value.type === 'none') newAggregate.type = null;
    emit('update:modelValue', newAggregate);
}

watch(() => props.modelValue, () => {
    setValuesFromModelValue();
});

watch(() => aggregate.value, () => {
    emitDefaultValues();
}, { deep: true });

function initialise (): void {
    if (props.modelValue) {
        setValuesFromModelValue();
    } else {
        emitDefaultValues();
    }
}

initialise();
</script>

<style lang="scss" scoped>
.granularity-container {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);

    @include responsive('sm') {
        gap: var(--defaultSpacing);
    }

    .select-option-flex {
        display: flex;
        flex-wrap: wrap;
        row-gap: var(--smallSpacing);
    }

    .delta-input {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--defaultSpacing);
    }
}

</style>
